/**
 * Auth0 configuration for the web client.
 *
 * Environment variables (set in `.env*` files or by the build pipeline):
 * - VITE_AUTH0_DOMAIN — Auth0 tenant domain, e.g. "your-tenant.auth0.com"
 * - VITE_AUTH0_CLIENT_ID — Auth0 application client ID
 * - VITE_AUTH0_AUDIENCE — Auth0 API audience (e.g. "https://bevi-ailab/api")
 *
 * Dev-mode override:
 * - VITE_AUTH_DEV_MODE=true — skip Auth0, use X-Dev-User header with VITE_AUTH_DEV_EMAIL.
 */

export interface Auth0Config {
  domain: string;
  clientId: string;
  audience: string;
  redirectUri: string;
}

export function isDevMode(): boolean {
  return import.meta.env.VITE_AUTH_DEV_MODE === 'true';
}

export function devEmail(): string {
  const fromEnv = import.meta.env.VITE_AUTH_DEV_EMAIL;
  if (fromEnv) return fromEnv;
  // E2E tests inject the dev email at runtime via addInitScript so each test can pick its own
  // identity (see web/e2e/fixtures.ts). Only consulted when build-time env is empty.
  if (typeof window !== 'undefined') {
    const w = window as unknown as { __VITE_AUTH_DEV_EMAIL__?: string };
    if (w.__VITE_AUTH_DEV_EMAIL__) return w.__VITE_AUTH_DEV_EMAIL__;
  }
  return '';
}

export function isAuth0Configured(): boolean {
  if (isDevMode()) return false;
  return Boolean(
    import.meta.env.VITE_AUTH0_DOMAIN &&
      import.meta.env.VITE_AUTH0_CLIENT_ID &&
      import.meta.env.VITE_AUTH0_AUDIENCE,
  );
}

/** Returns null if any required env var is missing — callers should render a diagnostic instead of throwing. */
export function getAuth0Config(): Auth0Config | null {
  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;
  if (!domain || !clientId || !audience) return null;
  return {
    domain,
    clientId,
    audience,
    redirectUri: typeof window !== 'undefined' ? window.location.origin : '',
  };
}
